// BadgeDisplay.js — SVG badge renderer
// All badge artwork lives here — swap out SVGs when Canva designs are ready

import React from 'react';

// True 10-point star polygon points (mathematically calculated)
const STAR10 = "40,4 44.9,24.8 61.2,10.9 52.9,30.6 74.2,28.9 56,40 74.2,51.1 52.9,49.4 61.2,69.1 44.9,55.2 40,76 35.1,55.2 18.8,69.1 27.1,49.4 5.8,51.1 24,40 5.8,28.9 27.1,30.6 18.8,10.9 35.1,24.8";

// Hexagon points (mathematically calculated)
const HEXAGON = "40,4 71.2,22 71.2,58 40,76 8.8,58 8.8,22";

// Shield path
const SHIELD = "M40,6 L70,20 L70,48 Q70,65 40,76 Q10,65 10,48 L10,20 Z";

// Dark background colors per category
const BG_COLORS = {
  '#14b8a6': '#1a3a3a',
  '#0ea5e9': '#1a2a3a',
  '#f59e0b': '#2a1e08',
  '#f97316': '#2a1a08',
  '#a855f7': '#2a1a2e',
  '#ec4899': '#2a1020',
  '#6366f1': '#1a1a3a',
  '#22c55e': '#0a2a1a',
  '#eab308': '#2a2208',
  '#10b981': '#0a2a1a',
  '#8b5cf6': '#1e1a3a',
};

const getBgColor = (color) => BG_COLORS[color] || '#1a2a2a';

export function BadgeDisplay({ badge, size = 80, locked = false }) {
  const { shape, color, icon, name } = badge;
  const opacity = locked ? 0.3 : 1;
  const bg = getBgColor(color);

  const commonDefs = (id) => (
    <defs>
      <radialGradient id={`bg_${id}`} cx="38%" cy="32%" r="65%">
        <stop offset="0%" stopColor={bg} />
        <stop offset="100%" stopColor="#0a0e1a" />
      </radialGradient>
    </defs>
  );

  const renderShape = (id) => {
    switch (shape) {
      case 'star10':
        return (
          <>
            {commonDefs(id)}
            <polygon
              points={STAR10}
              fill={`url(#bg_${id})`}
              stroke={color}
              strokeWidth="1.4"
            />
          </>
        );
      case 'circle':
        return (
          <>
            {commonDefs(id)}
            <circle
              cx="40" cy="40" r="37"
              fill={`url(#bg_${id})`}
              stroke={color}
              strokeWidth="1.4"
            />
          </>
        );
      case 'square':
        return (
          <>
            {commonDefs(id)}
            <rect
              x="4" y="4" width="72" height="72" rx="14"
              fill={`url(#bg_${id})`}
              stroke={color}
              strokeWidth="1.4"
            />
          </>
        );
      case 'shield':
        return (
          <>
            {commonDefs(id)}
            <path
              d={SHIELD}
              fill={`url(#bg_${id})`}
              stroke={color}
              strokeWidth="1.4"
            />
          </>
        );
      case 'hexagon':
        return (
          <>
            {commonDefs(id)}
            <polygon
              points={HEXAGON}
              fill={`url(#bg_${id})`}
              stroke={color}
              strokeWidth="1.8"
            />
            <polygon
              points={HEXAGON}
              fill="none"
              stroke={color}
              strokeWidth="0.5"
              opacity="0.25"
              transform="translate(40,40) scale(0.78) translate(-40,-40)"
            />
          </>
        );
      default:
        return null;
    }
  };

  // Icon positioning varies by shape
  const iconY = shape === 'shield' ? '46' : shape === 'square' ? '36' : '34';
  const numY = shape === 'shield' ? '62' : shape === 'square' ? '60' : '58';

  const uniqueId = `${badge.id}_${Math.random().toString(36).substr(2, 6)}`;

  // Special case: Habit scroll uses checkmark instead of text icon
  const renderIcon = () => {
    if (badge.id === 'scroll_habit') {
      return (
        <path
          d="M27,42 L36,51 L54,32"
          fill="none"
          stroke={color}
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      );
    }
    return (
      <text
        x="40"
        y={iconY}
        textAnchor="middle"
        fill={color}
        fontSize={shape === 'star10' ? '18' : '22'}
        fontFamily="serif"
      >
        {icon}
      </text>
    );
  };

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 80 80"
      xmlns="http://www.w3.org/2000/svg"
      style={{ opacity, flexShrink: 0 }}
      aria-label={name}
    >
      {renderShape(uniqueId)}
      {renderIcon()}
    </svg>
  );
}

// Large version for the popup celebration
export function BadgeDisplayLarge({ badge }) {
  return <BadgeDisplay badge={badge} size={120} />;
}

// Locked/unearned version — muted
export function BadgeDisplayLocked({ badge }) {
  return <BadgeDisplay badge={badge} size={80} locked={true} />;
}

export default BadgeDisplay;